//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { Box, Text } from "@chakra-ui/react";
import moment from "moment";
import React from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { CreditUsageBucket, Granularity } from "../../../api/usage/v1/usage";
import { useResolveDeploymentNames } from "./useResolveDeploymentNames";
import { formatBucketLabel } from "./usageRange";

// Sourced from this app's own semantic colour ramps (src/semantic/semantic.css),
// not invented hexes, so the chart reads as part of this UI rather than a
// generic chart library default. Muted/deep steps rather than each ramp's
// brighter 400-500 tier - those read as too vivid/saturated for a dashboard
// surface. In order: --green-600, --blue-600, --yellow-700, --red-700,
// --orange-600, --green-500, --blue-700, --red-500. The first 5 are a
// different hue family each (every chromatic family this design system has);
// slots 6-8 necessarily reuse a family at a different shade, since there are
// only 5 chromatic families - `gray` was tried as a 6th family but is exactly
// achromatic (OKLCH chroma 0) and fails the chroma floor outright (reads as a
// disabled/muted state, not a series), so it was rejected rather than shipped
// failing.
// Order matters, not just membership: an 8-colour set can pass "some ordering
// exists" while a bad one fails adjacent-pair separation once stacked, so the
// FIRST 5 (what most organizations - 5 or fewer deployments - actually see)
// were fixed first and slots 6-8 chosen and re-validated against that fixed
// prefix, not solved independently.
// Validated with the dataviz skill's validate_palette.js across all 8 in this
// exact order: lightness band, chroma floor, adjacent-pair CVD separation
// (worst 8.3 dE, >= the 8.0 target), normal-vision separation (worst 17.3 dE,
// >= the 15 hard floor), and contrast vs. the white chart surface all PASS.
// A palette large enough for the deployments a single organization
// realistically runs. Colours repeat beyond this, which is acceptable for a
// legend the viewer can read.
const SERIES_COLORS = ["#006532", "#0284c7", "#a17c07", "#b3161a", "#ea670c", "#007339", "#036ba1", "#e63035"];

// Label used for a bucket that has stackByDeployment=true but no deployment_id.
// See buildChartData below for why such a bucket must still be rendered under
// SOME series rather than silently dropped.
export const FALLBACK_DEPLOYMENT_LABEL = "Unknown deployment";

export type ChartRow = Record<string, string | number>;

// Rows reserve the bare "period" key for the X axis value, so every series
// row-key below is namespaced under "series:" and can never equal it.
//
// A flat prefix alone is not enough, though: the fallback series (a bucket
// with no deployment_id) and a REAL deployment whose resolved display name
// happens to be exactly FALLBACK_DEPLOYMENT_LABEL ("Unknown deployment")
// would still produce the identical prefixed key and silently merge. To
// prevent that, the fallback series (and the single-series "Credits" case)
// use a reserved sentinel key that is NOT derived from any user-controlled
// data at all, while a named, per-deployment series is namespaced separately
// under "series:id:<deployment_id>". A reserved sentinel can never collide
// with an id-derived key because their prefixes differ as whole segments
// ("series:fallback" / "series:total" vs "series:id:...").
//
// Keyed by deployment_id, NOT by the resolved display name: deployment names
// are only validated as required, not unique - they can and do repeat across
// projects. Keying by name collapsed two distinct deployments that happened
// to share a name into one series (and one stacked colour), silently merging
// their usage. IDs are the one thing guaranteed unique here.
//
// The row/series keys are internal only; callers must go through
// seriesDisplayName (passing the `displayNames` map buildChartData returns)
// to recover the human-readable name for the legend/tooltip.
const CREDITS_KEY = "series:total";
const FALLBACK_KEY = "series:fallback";
const ID_PREFIX = "series:id:";

const idSeriesKey = (id: string): string => `${ID_PREFIX}${id}`;

// Inverse of the key builders above - recovers the human-readable name for a
// series key. CREDITS_KEY/FALLBACK_KEY have a fixed label; an id-keyed series
// has no human-readable information in the key itself (unlike the old
// name-keyed scheme), so its label must come from `displayNames` - the map
// buildChartData returns, already disambiguated for any colliding names (see
// buildChartData below). Falls back to the raw id if, somehow, the key is not
// in the map at all, so the legend never renders truly blank.
export const seriesDisplayName = (key: string, displayNames: ReadonlyMap<string, string>): string => {
  if (key === CREDITS_KEY) {
    return "Credits";
  }
  if (key === FALLBACK_KEY) {
    return FALLBACK_DEPLOYMENT_LABEL;
  }
  return displayNames.get(key) ?? key.slice(ID_PREFIX.length);
};

// disambiguatedDisplayName appends a short id fragment to a name that two or
// more distinct deployment ids resolved to, so the legend still lets a human
// tell the (now two-or-more) series apart. Left alone otherwise - only an
// actual collision is uglified, not the common one-deployment-one-name case.
const disambiguatedDisplayName = (name: string, id: string, collidingIdCount: number): string => (collidingIdCount > 1 ? `${name} (${id.slice(-6)})` : name);

// buildChartData pivots the flat bucket list into one row per period, one
// column per series. Exported (pure, no React) so it can be unit tested
// directly instead of through a rendered <BarChart>.
export const buildChartData = (
  buckets: CreditUsageBucket[],
  granularity: Granularity,
  stackByDeployment: boolean,
  nameFor: (id: string) => string
): { rows: ChartRow[]; series: string[]; displayNames: Map<string, string> } => {
  const rowsByPeriod = new Map<string, ChartRow>();
  // series is derived from the keys actually written into rows, not computed
  // separately from bucket.deployment_id. That is deliberate: any series key
  // written into a row is added here in the same step, so a row key can
  // never end up missing from this list (see FALLBACK_DEPLOYMENT_LABEL below).
  const seriesKeys = new Set<string>();
  // Raw (pre-disambiguation) resolved name for each id-keyed series, keyed by
  // the same series key - used below to detect and disambiguate collisions.
  const rawNameByKey = new Map<string, string>();

  buckets.forEach((bucket) => {
    if (!bucket.period_start) {
      // period_start is optional on the wire type. moment.utc(undefined) does
      // NOT throw - it resolves to the current instant - so an unguarded
      // conversion would silently plot a missing timestamp as "today". That
      // directly contradicts usageRangeFor's invariant that today is excluded
      // because its usage isn't priced yet, so such a bucket is dropped
      // instead of being mislabelled. This should not happen with a
      // well-formed response; if it does, it is safer to lose one bucket's
      // credits from the chart than to fabricate a phantom "today" bar.
      return;
    }
    const periodStart = moment.utc(bucket.period_start).toDate();
    const label = formatBucketLabel(periodStart, granularity);
    const row = rowsByPeriod.get(label) || { period: label };
    let key: string;
    if (!stackByDeployment) {
      key = CREDITS_KEY;
    } else if (bucket.deployment_id) {
      key = idSeriesKey(bucket.deployment_id);
      rawNameByKey.set(key, nameFor(bucket.deployment_id));
    } else {
      key = FALLBACK_KEY;
    }
    row[key] = ((row[key] as number) || 0) + (bucket.credits || 0);
    rowsByPeriod.set(label, row);
    seriesKeys.add(key);
  });

  const rows = Array.from(rowsByPeriod.values()).sort((a, b) => String(a.period).localeCompare(String(b.period)));
  const series = Array.from(seriesKeys);

  // Count how many distinct series keys resolved to each raw name, so a
  // collision (two different deployment ids sharing a display name) can be
  // disambiguated in the legend without touching the (already unique, by
  // construction) row/series keys themselves.
  const keysByRawName = new Map<string, string[]>();
  rawNameByKey.forEach((name, key) => {
    const keys = keysByRawName.get(name) || [];
    keys.push(key);
    keysByRawName.set(name, keys);
  });

  const displayNames = new Map<string, string>();
  rawNameByKey.forEach((name, key) => {
    const collidingKeys = keysByRawName.get(name) || [];
    displayNames.set(key, disambiguatedDisplayName(name, key.slice(ID_PREFIX.length), collidingKeys.length));
  });

  return { rows, series, displayNames };
};

// activeLabelFromChartClick extracts the clicked period's label from a
// recharts BarChart click payload (CategoricalChartState), or undefined if
// the click did not land on a usable X-axis position - e.g. a click just
// outside the plotted bars. Exported (pure, no React/recharts rendering
// involved) so the guard can be exercised directly with a payload shaped the
// way recharts actually delivers it, the same way buildChartData above is
// tested without needing a rendered <BarChart>.
export const activeLabelFromChartClick = (state: { activeLabel?: unknown } | null | undefined): string | undefined =>
  typeof state?.activeLabel === "string" ? state.activeLabel : undefined;

export const CreditUsageChart = ({
  buckets,
  granularity,
  stackByDeployment = false,
  onPeriodClick,
}: {
  buckets: CreditUsageBucket[];
  granularity: Granularity;
  stackByDeployment?: boolean;
  // Called with a bucket's period label (exactly as formatBucketLabel
  // produced it - see periodBoundsFor for turning it back into a date range)
  // when the viewer clicks anywhere on that period's column, regardless of
  // which stacked series segment the click landed on.
  onPeriodClick?: (period: string) => void;
}) => {
  const deploymentIds = Array.from(new Set(buckets.map((b) => b.deployment_id || "").filter((id) => !!id)));
  const { nameFor } = useResolveDeploymentNames(deploymentIds);

  if (!buckets.length) {
    return (
      <Box padding="32px 0">
        <Text>No usage recorded yet.</Text>
      </Box>
    );
  }

  const { rows, series, displayNames } = buildChartData(buckets, granularity, stackByDeployment, nameFor);

  return (
    <Box height="320px" cursor={onPeriodClick ? "pointer" : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          onClick={(state) => {
            // activeLabel is the XAxis ("period") value under the click,
            // regardless of which stacked <Bar> segment (or the gap between
            // them, within the column's hit area) was actually clicked - a
            // per-<Bar> onClick would only fire for that one series' segment
            // and miss the rest of the stack.
            const label = activeLabelFromChartClick(state);
            if (onPeriodClick && label) {
              onPeriodClick(label);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          {series.map((key, index) => (
            <Bar key={key} dataKey={key} name={seriesDisplayName(key, displayNames)} stackId="credits" fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
